import {observer} from 'mobx-react';
import React, {useContext, useEffect, useRef, useState} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import {useToasts} from 'react-toast-notifications';
import {Container} from 'reactstrap';
import styled from 'styled-components';
import {useMap} from '../../components/map/hooks';
import useMapDevice from '../../components/map/hooks/useMapDevice';
import useMapEvent from '../../components/map/hooks/useMapEvent';
import useMapGeofences from '../../components/map/hooks/useMapGeofences';
import useMapLabel from '../../components/map/hooks/useMapLabel';
import useMapPosition from '../../components/map/hooks/useMapPosition';
import useMapReport from '../../components/map/hooks/useMapReport';
import useMapTrace from '../../components/map/hooks/useMapTrace';
import useDevices from '../../hooks/use-devices';
import {useNotificationClick} from '../../helpers/notificationClick';
import useStore from '../../hooks/use-store';
import DeviceDetail from '../../views/event-map/device-detail';
import PopupEvent from '../../views/map/popupEvent';
import MapRightActions from '../../views/map-right-actions';
import {useIntl} from 'react-intl';
import useServices from '../../hooks/use-services';
import EventFormHandler from '../../views/event-map/event-form-handle';
import {ThemeContext} from '../../apiContexts';

const Map = styled.div`
    height: 100%;
    width: 100%;
    position: relative;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    border-radius: 12px;

    .ol-viewport {
        border-radius: 12px;
    }

    #right-actions {
        position: absolute;
        right: 1rem;
        z-index: 10;
        top: 1rem;
    }
`;

const AccessDeniedOverlay = styled.div`
    position: absolute;
    inset: 0;
    z-index: 20;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    background-color: #fff;
    border-radius: 12px;
    text-align: center;
    padding: 1rem;
`;

const AdminEventMapContainer = ({location, ...props}) => {
    const history = useHistory();
    const notificationClick = useNotificationClick();
    const {eventId, deviceId} = useParams();
    const {store} = useStore();
    const {events, auth, customerStore} = store;
    const {eventService, deviceService} = useServices();
    const {devices} = useDevices();
    const {addToast} = useToasts();
    const mapRef = useRef();
    const intl = useIntl();
    const theme = useContext(ThemeContext);
    const [deviceAccessDenied, setDeviceAccessDenied] = useState(false);

    const {
        instance,
        state,
        setEvent,
        geofenceFocus,
        setTrace,
        follow,
        setFollow,
        trace,
        // toogleTrace: traceToggleCurrentDevice,
        label,
        toggleLabel,
        selectedMap,
        setSelectedMap,
        showDevices,
        setShowDevices: toggleDevice,
        showGeofences,
        setShowGeofences,
        createGeofenceForCurrentDevice,
    } = useMap(
        {
            target: mapRef,
            center: store.map.olCenter,
            zoom: store.map.zoom,
            tiers: store.map.olCustomTiles,
        },
        useMapDevice,
        useMapPosition,
        useMapLabel,
        useMapReport,
        useMapGeofences,
        useMapEvent,
        useMapTrace,
    );

    useEffect(() => {
        if (
            devices.updatedPositions &&
            devices.list.length &&
            state.initializedPositions
        ) {
            if (eventId) {
                instance.removeDevices();
                instance.removeFeaturesTrace();
                setTrace(false);
                (async () => {
                    let event = events.findById(eventId);
                    if (!event) event = await eventService.get(eventId);
                    if (event) {
                        setEvent(event, notificationClick);
                    }
                })();
            }

            if (deviceId) {
                const device = store.devices.findById(+deviceId);
                if (!device) {
                    setDeviceAccessDenied(true);
                    return;
                }
                setDeviceAccessDenied(false);
                // setEvent(null) so limpa o estado local do hook de mapa -- events.current/
                // events.position (store) ficavam com o evento anterior, e DeviceDetail lia essa
                // posicao antiga em vez da atual do veiculo.
                setEvent(null);
                events.setCurrent(null);
                events.setPosition(null);
                events.setCurrentDevice(+deviceId);
                instance.removeDevices();
                instance.initTraceLayer();
                instance.addFeatureDevice(device);
                setTrace(false);

                (async () => {
                    const position = device.currentPosition;

                    if (!position) return;

                    const {longitude, latitude} = position;
                    instance.goTo([longitude, latitude], 18);
                })();
            }
        }
    }, [
        instance,
        auth,
        events,
        eventId,
        deviceId,
        // BUSINESS RULE: cada clique numa notificação grava um valor novo no estado da localização
        // (`helpers/notificationClick`), e o efeito refaz a seleção: mostra o evento ou leva o mapa ao
        // veículo de novo, mesmo com a mesma URL e com o mapa movido desde o clique anterior. Outra
        // mudança de localização (hash, fechar um modal), mesmo sem estado, não repete a seleção.
        notificationClick,
        devices.list.length,
        state.initializedPositions,
        devices.updatedPositions,
        setTrace,
        setEvent,
        store.devices,
    ]);

    // BUSINESS RULE: o ajuste à cerca mexe na visão, e o acompanhamento ao vivo (ligado na tela de um
    // veículo, na mesma rota) a puxaria de volta ao veículo na próxima posição. Desliga quando o ajuste
    // chega ao mapa, também o de uma lista de cercas tardia; área ilegível não o aplica e não o desliga.
    useEffect(() => {
        if (geofenceFocus) setFollow(false);
    }, [geofenceFocus, setFollow]);

    const followToggle = () => {
        const status = !follow;
        setFollow(status);
        if (status && events.currentDevice.hasPosition()) {
            devices.setSelected(events.currentDevice.id);
            const {longitude, latitude} = events.currentDevice.currentPosition;
            instance.goTo([longitude, latitude]);
        }
    };

    useEffect(() => {
        if (instance) {
            const map = instance.instance;
            instance.setI18n(intl);

            map.on('click', (event) => {
                const [feature, layer] =
                map.forEachFeatureAtPixel(event.pixel, (feature, layer) => {
                    return [feature, layer];
                }) || [];

                if (!layer) return;

                const layerName = layer.get('name');

                if (layerName === 'devicesLayer') {
                    const features = feature.get('features');
                    if (features && features.length === 1) devices.setSelected(features[0].getId());
                }
            });

            map.on('pointermove', (event) => {
                map.getTargetElement().style.cursor = map.hasFeatureAtPixel(event.pixel) ?
                    'pointer' :
                    '';
            });

            map.on('pointerdrag', (event) => {
                instance.isFollowAnimating = false;
                setFollow(false);
            });

            let zoomCache;
            map.on('movestart', (event) => {
                zoomCache = event.map.getView().getZoom();
            });

            map.on('moveend', (event) => {
                if (instance.isFollowAnimating) {
                    instance.isFollowAnimating = false;
                    return;
                }
                const zoom = event.map.getView().getZoom();
                zoom !== zoomCache && setFollow(false);
            });
        }
    }, [auth, instance, store.devices]);

    const toggleTrace = () => {
        setTrace(!trace, devices.selectedDevice);
    };

    const toggleGeofences = () => {
        setShowGeofences(!showGeofences);
    };

    const onChangeMap = (value) => {
        setSelectedMap(value);
    };

    const getDrive = () => {
        return events.currentDevice.currentPosition && events.currentDevice.currentPosition.attributes ?
            store.drivers.getDriverByUniqueId(events.currentDevice.currentPosition.attributes.driverUniqueId) : null;
    };

    const sendComment = async (comment) => {
        try {
            await eventService.sendComment({eventId, comment, attributes: {}});

            addToast('Tratamento de evento realizado com sucesso', {appearance: 'success', autoDismiss: true});
            events.remove(eventId);
            history.push('/admin');
        } catch (e) {
            console.log(e);
        }
    };

    const getCustomer = () => {
        return customerStore.findById(events.currentDevice.customerId);
    };

    return (
        <Container fluid className="px-4 pb-4 position-relative h-100">
            {devices.list.length > 0 && <PopupEvent isEvent={!!eventId}/>}

            {devices.list.length > 0 && events.currentDevice && (
                <DeviceDetail
                    toggleTrace={toggleTrace}
                    trace={trace}
                    createGeofence={createGeofenceForCurrentDevice}
                    // Acompanhar ao vivo nao existe na tela do evento: ela e o registro daquele
                    // instante. Sem o gatilho, o botao nao aparece.
                    followToogle={eventId ? null : followToggle}
                    follow={eventId ? false : follow}
                    driver={getDrive()}
                    customer={getCustomer()}
                />
            )}
            <Map ref={mapRef} className="custom-shadow-2">
                {deviceAccessDenied && (
                    <AccessDeniedOverlay>
                        <span>{intl.formatMessage({id: 'deviceAccessDenied'})}</span>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => history.push('/admin')}
                        >
                            {intl.formatMessage({id: 'deviceAccessDeniedBackToDashboard'})}
                        </button>
                    </AccessDeniedOverlay>
                )}
                <MapRightActions
                    map={instance}
                    toggleTrace={toggleTrace}
                    trace={trace}
                    toggleLabel={toggleLabel}
                    label={label}
                    onChangeMap={onChangeMap}
                    selectedMap={selectedMap}
                    toggleDevice={toggleDevice}
                    showDevices={showDevices}
                    toggleGeofences={toggleGeofences}
                    showGeofences={showGeofences}
                    close={'/admin'}
                />

                {eventId && <EventFormHandler
                    theme={theme}
                    submit={sendComment}/>}
            </Map>
        </Container>
    );
};

export default observer(AdminEventMapContainer);
